import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Phone, Sparkles, ShieldCheck, ArrowRight, Loader2, CheckCircle2, XCircle, Handshake, Tag } from "lucide-react";
import { branding } from "@/config/branding";
import { Button, Input, Select, toast } from "@/components/ds";
import { BrandLogo } from "@/components/BrandLogo";
import { SmsConsent } from "@/components/SmsConsent";
import { api, formatApiError } from "@/lib/api";

const INDUSTRIES = [
  { value: "hvac", label: "HVAC" },
  { value: "plumbing", label: "Plumbing" },
  { value: "roofing", label: "Roofing" },
  { value: "electrical", label: "Electrical" },
];

const money = (n: number | null | undefined) => (n == null ? "" : `$${n.toLocaleString()}`);
const fmt = (n: number) => `$${n.toLocaleString(undefined, { minimumFractionDigits: Number.isInteger(n) ? 0 : 2, maximumFractionDigits: 2 })}`;
const GROWTH_INTRO_PCT = 0.25;

export default function GetStarted() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const planParam = (params.get("plan") || "starter").toLowerCase();
  const repRef = params.get("rep") || undefined;

  const [plans, setPlans] = useState<any[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [codeState, setCodeState] = useState<{ status: "idle" | "checking" | "valid" | "invalid"; info?: any; error?: string }>({ status: "idle" });
  // A2P 10DLC: consent is captured at the point of opt-in and is NEVER pre-checked.
  const [smsConsent, setSmsConsent] = useState(false);
  const [smsMarketingConsent, setSmsMarketingConsent] = useState(false);
  const [f, setF] = useState({
    first_name: "", last_name: "", business_name: "", email: "", phone: "",
    industry: "hvac", city: "", state: "", plan_type: ["starter", "growth", "pro"].includes(planParam) ? planParam : "starter",
    referral_code: "",
  });

  useEffect(() => { api.publicPlans().then(setPlans).catch(() => setPlans([])); }, []);
  const set = (k: string, v: string) => setF((p) => ({ ...p, [k]: v }));
  const plan = useMemo(() => (plans || []).find((p) => p.plan_type === f.plan_type), [plans, f.plan_type]);

  const REASONS: Record<string, string> = {
    not_found: "We couldn't find that code. Please check it and try again.",
    expired: "This code has expired.",
    inactive: "This code is no longer active.",
    max_redemptions_reached: "This code has reached its redemption limit.",
    empty: "Please enter a code.",
  };

  const applyCode = async () => {
    const code = f.referral_code.trim();
    if (!code) { setCodeState({ status: "idle" }); return; }
    setCodeState({ status: "checking" });
    try {
      const res = await api.validateCode(code);
      if (res.valid) setCodeState({ status: "valid", info: res });
      else setCodeState({ status: "invalid", error: REASONS[res.reason || ""] || "This code can't be applied." });
    } catch (err) {
      setCodeState({ status: "invalid", error: formatApiError(err) });
    }
  };
  const clearCode = () => { set("referral_code", ""); setCodeState({ status: "idle" }); };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    for (const [k, v] of Object.entries(f)) {
      if (k !== "referral_code" && !String(v).trim()) { toast.error("Please complete all required fields."); return; }
    }
    if (!smsConsent) {
      toast.error("Please agree to receive SMS messages to continue."); return;
    }
    // Block checkout if a code was typed but is known-invalid (server remains authoritative regardless).
    if (f.referral_code.trim() && codeState.status === "invalid") {
      toast.error("Please fix or remove the code before continuing."); return;
    }
    setBusy(true);
    try {
      const res = await api.publicCheckout({
        ...f,
        referral_code: f.referral_code || undefined,
        rep: repRef,
        sms_consent: smsConsent,
        sms_marketing_consent: smsMarketingConsent,
      });
      window.location.href = res.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err)); setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex h-16 max-w-5xl items-center justify-between px-4 sm:px-6">
          <Link to="/" className="flex items-center" data-testid="getstarted-logo">
            <BrandLogo className="h-8" />
          </Link>
          <Link to="/pricing" className="text-sm font-medium text-slate-600 hover:text-slate-900">View plans</Link>
        </div>
      </header>

      <main className="mx-auto grid max-w-5xl gap-8 px-4 py-10 sm:px-6 lg:grid-cols-[1fr_360px]">
        <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="getstarted-form">
          <h1 className="font-display text-2xl font-semibold tracking-tight text-slate-900">Let's get your AI front desk started</h1>
          <p className="mt-1 text-sm text-slate-500">Tell us about your business. Our team uses this to begin your professional setup after payment.</p>

          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <Field label="First name"><Input value={f.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="gs-first-name" /></Field>
            <Field label="Last name"><Input value={f.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="gs-last-name" /></Field>
            <Field label="Business name" full><Input value={f.business_name} onChange={(e) => set("business_name", e.target.value)} data-testid="gs-business-name" /></Field>
            <Field label="Business email"><Input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="gs-email" /></Field>
            <Field label="Phone number"><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="gs-phone" /></Field>
            <Field label="Industry"><Select value={f.industry} onChange={(v) => set("industry", v)} testid="gs-industry" options={INDUSTRIES} /></Field>
            <Field label="Plan"><Select value={f.plan_type} onChange={(v) => set("plan_type", v)} testid="gs-plan" options={[
              { value: "starter", label: "Starter" }, { value: "growth", label: "Growth" }, { value: "pro", label: "Pro" },
            ]} /></Field>
            <Field label="City"><Input value={f.city} onChange={(e) => set("city", e.target.value)} data-testid="gs-city" /></Field>
            <Field label="State"><Input value={f.state} onChange={(e) => set("state", e.target.value)} data-testid="gs-state" /></Field>
            <div className="sm:col-span-2" data-testid="promo-code-block">
              <label className="mb-1 block text-xs font-medium text-slate-500">Have a promo or referral code?</label>
              <div className="flex gap-2">
                <Input
                  value={f.referral_code}
                  onChange={(e) => { set("referral_code", e.target.value); if (codeState.status !== "idle") setCodeState({ status: "idle" }); }}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyCode(); } }}
                  placeholder="Enter your code"
                  disabled={codeState.status === "valid"}
                  data-testid="gs-referral"
                />
                {codeState.status === "valid" ? (
                  <Button type="button" variant="outline" onClick={clearCode} data-testid="gs-code-clear">Remove</Button>
                ) : (
                  <Button type="button" variant="outline" onClick={applyCode} disabled={codeState.status === "checking" || !f.referral_code.trim()} data-testid="gs-code-apply">
                    {codeState.status === "checking" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}
                  </Button>
                )}
              </div>

              {codeState.status === "valid" && codeState.info?.code_type === "partner_referral" && (
                <div className="mt-2 flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3" data-testid="code-partner-applied">
                  <Handshake className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                  <div>
                    <p className="text-sm font-semibold text-emerald-800">Partner Referral Applied</p>
                    <p className="text-xs text-emerald-700">
                      {codeState.info.setup_discount_percent ? `${codeState.info.setup_discount_percent}% off setup` : ""}
                      {codeState.info.setup_discount_percent && codeState.info.subscription_discount_percent ? " · " : ""}
                      {codeState.info.subscription_discount_percent ? `${codeState.info.subscription_discount_percent}% off monthly` : ""}
                    </p>
                  </div>
                </div>
              )}
              {codeState.status === "valid" && codeState.info?.code_type === "discount" && (
                <div className="mt-2 flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3" data-testid="code-promo-applied">
                  <Tag className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                  <div>
                    <p className="text-sm font-semibold text-emerald-800">Promotion Applied</p>
                    <p className="text-xs text-emerald-700">Your discount will be applied at checkout.</p>
                  </div>
                </div>
              )}
              {codeState.status === "invalid" && (
                <div className="mt-2 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3" data-testid="code-error">
                  <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
                  <p className="text-sm text-red-700">{codeState.error}</p>
                </div>
              )}
            </div>
          </div>

          <SmsConsent
            checked={smsConsent}
            onChange={setSmsConsent}
            showMarketing
            marketingChecked={smsMarketingConsent}
            onMarketingChange={setSmsMarketingConsent}
          />

          <Button type="submit" className="mt-6 w-full" disabled={busy} data-testid="gs-continue">
            {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Redirecting to secure checkout…</> : <>Continue to secure payment <ArrowRight className="ml-1 h-4 w-4" /></>}
          </Button>
          <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-slate-400"><ShieldCheck className="h-3.5 w-3.5" /> Payment is processed securely by Stripe. You won't get dashboard access until our team finishes and tests your setup.</p>
        </form>

        <aside className="h-fit rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="getstarted-summary">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Your plan</p>
          <p className="mt-1 font-display text-xl font-semibold text-slate-900">{plan?.name ?? "—"}</p>
          {plan && (
            <div className="mt-4 space-y-2 text-sm">
              {plan.plan_type === "growth" && codeState.status !== "valid" ? (
                <div data-testid="gs-growth-promo">
                  <div className="mb-2 inline-flex items-center gap-1 rounded-full bg-emerald-600 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-white">
                    <Sparkles className="h-3 w-3" /> 25% OFF FIRST MONTH
                  </div>
                  <PromoRow label="First month" was={money(plan.monthly_price)} now={fmt(plan.monthly_price * (1 - GROWTH_INTRO_PCT))} />
                  <Row label="One-time professional setup" value={money(plan.setup_fee)} />
                  <div className="mt-2 flex items-center justify-between border-t border-slate-100 pt-2">
                    <span className="font-medium text-slate-900">Due today</span>
                    <span className="flex items-center gap-2">
                      <span className="text-xs text-slate-400 line-through">{money(plan.monthly_price + plan.setup_fee)}</span>
                      <span className="font-semibold text-emerald-700" data-testid="gs-growth-due-today">{fmt(plan.monthly_price * (1 - GROWTH_INTRO_PCT) + plan.setup_fee)}</span>
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-slate-500">Discount applies to the first month only. The one-time setup fee is not discounted. Renews at {money(plan.monthly_price)}/month starting with the second month.</div>
                </div>
              ) : (
                <>
                  <Row label="Monthly subscription" value={`${money(plan.monthly_price)}/mo`} />
                  <Row label="One-time professional setup" value={money(plan.setup_fee)} />
                  <div className="border-t border-slate-100 pt-2 text-xs text-slate-500">Taxes calculated at checkout. Setup fee and subscription are billed as separate line items.</div>
                </>
              )}
            </div>
          )}
          <div className="mt-5 rounded-lg border border-blue-100 bg-blue-50/60 p-3">
            <p className="flex items-center gap-1.5 text-xs font-semibold text-blue-800"><Sparkles className="h-3.5 w-3.5" /> What the setup fee includes</p>
            <p className="mt-1 text-xs text-slate-600">Configuration of your account, AI receptionist, business knowledge, scheduling rules, supported integrations, testing and launch preparation — done for you by the ResFil team.</p>
          </div>
        </aside>
      </main>
    </div>
  );
}

function Field({ label, children, full }: { label: string; children: React.ReactNode; full?: boolean }) {
  return <div className={full ? "sm:col-span-2" : ""}><label className="mb-1 block text-xs font-medium text-slate-500">{label}</label>{children}</div>;
}
function Row({ label, value }: { label: string; value: string }) {
  return <div className="flex items-center justify-between"><span className="text-slate-500">{label}</span><span className="font-medium text-slate-900">{value}</span></div>;
}
function PromoRow({ label, was, now }: { label: string; was: string; now: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-slate-500">{label}</span>
      <span className="flex items-center gap-2">
        <span className="text-xs text-slate-400 line-through">{was}</span>
        <span className="font-medium text-slate-900">{now}</span>
      </span>
    </div>
  );
}
